/**
 * useMapLayers hook.
 *
 * Manages layer data, visibility, and filtering.
 * Fetches data from the API and normalizes it into domain types.
 *
 * Uses the standard React async-effect pattern (local `cancelled` flag +
 * AbortController) so that React StrictMode's mount→cleanup→remount
 * cycle in development does not leave loading state stuck or kill the
 * second batch of requests.
 */

import { useState, useCallback, useEffect, useRef } from 'react';
import type {
  MapField,
  MapTree,
  MapFarmer,
  MapCohort,
  MapWarehouse,
  LayerVisibility,
  GeoCoord,
} from '../types/mapTypes';
import {
  fetchFarmPlots,
  fetchTrees,
  fetchFarmersGeo,
  fetchCohortsGeo,
  fetchWarehousesGeo,
  normalizeFarmPlots,
  normalizeTrees,
  normalizeFarmers,
  normalizeCohorts,
  normalizeWarehouses,
} from '../api/mapApi';


export interface MapLayerAccess {
  farmers?: boolean;
  trees?: boolean;
  cohorts?: boolean;
  warehouses?: boolean;
}

interface UseMapLayersOptions {
  autoFetch?: boolean;
  viewport?: { center: GeoCoord; zoom: number };
  /** Optional modules. A false flag means NO request and no stale data. */
  include?: MapLayerAccess;
}

interface UseMapLayersReturn {
  // Data
  fields: MapField[];
  trees: MapTree[];
  farmers: MapFarmer[];
  cohorts: MapCohort[];
  warehouses: MapWarehouse[];

  // Visibility
  visibility: LayerVisibility;
  setVisibility: React.Dispatch<React.SetStateAction<LayerVisibility>>;
  toggleLayer: (layer: keyof LayerVisibility) => void;

  // Loading
  loading: boolean;
  error: string | null;

  // Stats
  stats: {
    fieldCount: number;
    treeCount: number;
    farmerCount: number;
    cohortCount: number;
    warehouseCount: number;
  };

  // Actions
  refresh: () => Promise<void>;
  allCoords: GeoCoord[];
}

const DEFAULT_VISIBILITY: LayerVisibility = {
  fields: true,
  trees: true,
  farmers: true,
  cohorts: true,
  warehouses: true,
  activities: false,
  issues: false,
  adminBoundaries: false,
};

/**
 * Shared fetch-and-normalize logic.
 * `signal` is optional so callers can pass an AbortSignal or omit it.
 * Returns nothing — directly sets React state via the setters passed in.
 */
async function fetchAllLayers(
  setters: {
    setFields: React.Dispatch<React.SetStateAction<MapField[]>>;
    setTrees: React.Dispatch<React.SetStateAction<MapTree[]>>;
    setFarmers: React.Dispatch<React.SetStateAction<MapFarmer[]>>;
    setCohorts: React.Dispatch<React.SetStateAction<MapCohort[]>>;
    setWarehouses: React.Dispatch<React.SetStateAction<MapWarehouse[]>>;
    setError: React.Dispatch<React.SetStateAction<string | null>>;
  },
  signal?: AbortSignal,
  include: Required<MapLayerAccess> = { farmers: true, trees: true, cohorts: true, warehouses: true },
): Promise<void> {
  // A disabled module is skipped entirely and its state is cleared, so a
  // workspace switch cannot leave a revoked layer on screen.
  const settle = <T,>(enabled: boolean, request: () => Promise<T>): Promise<T> | null =>
    enabled ? request() : null;
  const results = await Promise.allSettled([
    fetchFarmPlots(undefined, signal),
    settle(include.trees, () => fetchTrees(undefined, signal)),
    settle(include.farmers, () => fetchFarmersGeo(undefined, signal)),
    settle(include.cohorts, () => fetchCohortsGeo(undefined, signal)),
    settle(include.warehouses, () => fetchWarehousesGeo(signal)),
  ]);

  if (signal?.aborted) return;

  const value = (index: number) => (results[index].status === 'fulfilled' ? results[index].value : null);
  if (results[0].status === 'fulfilled') setters.setFields(normalizeFarmPlots(results[0].value as any));
  setters.setTrees(include.trees && value(1) ? normalizeTrees(value(1) as any) : []);
  setters.setFarmers(include.farmers && value(2) ? normalizeFarmers(value(2) as any) : []);
  setters.setCohorts(include.cohorts && value(3) ? normalizeCohorts(value(3) as any) : []);
  setters.setWarehouses(include.warehouses && value(4) ? normalizeWarehouses(value(4) as any) : []);

  const failures = results.filter((r) => r.status === 'rejected');
  if (failures.length) {
    setters.setError(
      `${failures.length} map layer${failures.length === 1 ? '' : 's'} could not be loaded. Retry to restore them.`,
    );
  }
}

export function useMapLayers(options: UseMapLayersOptions = {}): UseMapLayersReturn {
  const { autoFetch = true, include } = options;
  const includeFarmers = include?.farmers !== false;
  const includeTrees = include?.trees !== false;
  const includeCohorts = include?.cohorts !== false;
  const includeWarehouses = include?.warehouses !== false;

  const [fields, setFields] = useState<MapField[]>([]);
  const [trees, setTrees] = useState<MapTree[]>([]);
  const [farmers, setFarmers] = useState<MapFarmer[]>([]);
  const [cohorts, setCohorts] = useState<MapCohort[]>([]);
  const [warehouses, setWarehouses] = useState<MapWarehouse[]>([]);
  const [visibility, setVisibility] = useState<LayerVisibility>(DEFAULT_VISIBILITY);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  // Shared setters object (stable references, safe to memoize once)
  const setters = useRef({ setFields, setTrees, setFarmers, setCohorts, setWarehouses, setError });
  setters.current = { setFields, setTrees, setFarmers, setCohorts, setWarehouses, setError };

  /**
   * Manual refresh — used by MapsPage after creating/updating a field.
   * Aborts any in-flight request first so we don't race with the
   * auto-fetch effect.
   */
  const refresh = useCallback(async () => {
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setLoading(true);
    setError(null);

    try {
      await fetchAllLayers(setters.current, controller.signal, {
        farmers: includeFarmers, trees: includeTrees, cohorts: includeCohorts, warehouses: includeWarehouses,
      });
    } catch (err) {
      if (controller.signal.aborted) return;
      const msg = err instanceof Error ? err.message : 'Failed to load map data';
      setError(msg);
      console.error('[MapLayers] Failed to load:', err);
    } finally {
      if (!controller.signal.aborted) setLoading(false);
    }
  }, [includeFarmers, includeTrees, includeCohorts, includeWarehouses]);

  // ── Auto-fetch on mount ────────────────────────────────────────
  //
  // Uses the standard React async-effect pattern:
  //   • A local `cancelled` flag prevents state updates after cleanup.
  //   • A local AbortController cancels the HTTP requests on cleanup.
  //   • On cleanup, both `cancelled` and `controller.abort()` fire.
  //   • If React StrictMode re-runs the effect, a *new* closure with
  //     `cancelled = false` and a fresh controller is created, so the
  //     second batch of requests succeeds even though the first was
  //     aborted.
  useEffect(() => {
    if (!autoFetch) return;

    let cancelled = false;
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    const run = async () => {
      setLoading(true);
      setError(null);

      try {
        await fetchAllLayers(setters.current, controller.signal, {
          farmers: includeFarmers, trees: includeTrees, cohorts: includeCohorts, warehouses: includeWarehouses,
        });
      } catch (err) {
        if (cancelled || controller.signal.aborted) return;
        const msg = err instanceof Error ? err.message : 'Failed to load map data';
        setError(msg);
        console.error('[MapLayers] Failed to load:', err);
      } finally {
        if (!cancelled && !controller.signal.aborted) setLoading(false);
      }
    };

    run();

    return () => {
      cancelled = true;
      controller.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoFetch, includeFarmers, includeTrees, includeCohorts, includeWarehouses]);

  const toggleLayer = useCallback((layer: keyof LayerVisibility) => {
    setVisibility((prev) => ({ ...prev, [layer]: !prev[layer] }));
  }, []);

  // Derived stats
  const stats = {
    fieldCount: fields.length,
    treeCount: trees.length,
    farmerCount: farmers.length,
    cohortCount: cohorts.length,
    warehouseCount: warehouses.length,
  };

  // All coordinates for bounds fitting
  const allCoords: GeoCoord[] = [
    ...fields.flatMap((f) => f.boundary),
    ...trees.map((t) => t.position),
    ...farmers.flatMap((f) => f.boundary),
    ...cohorts.flatMap((c) => c.boundary),
    ...warehouses.map((w) => w.position),
  ];

  return {
    fields,
    trees,
    farmers,
    cohorts,
    warehouses,
    visibility,
    setVisibility,
    toggleLayer,
    loading,
    error,
    stats,
    refresh,
    allCoords,
  };
}
